/**
 * StatsRow — top summary stat cards.
 */

import React from 'react';
import { Tooltip } from 'antd';
import { motion, useReducedMotion } from 'framer-motion';
import { Inbox, Send, Clock, TrendingUp, AlarmClock, MailOpen, RefreshCw, CheckCircle, Info, ArrowRight } from 'lucide-react';
import { STAGGER_CONTAINER, FADE_UP } from '../constants';

function fmtTime(mins) {
  if (mins === null || mins === undefined) return '—';
  if (mins < 60) return `${mins}m`;
  const h = Math.floor(mins / 60);
  const m = mins % 60;
  return m > 0 ? `${h}h ${m}m` : `${h}h`;
}

export function StatsRow({ metrics, isDark, delayThreshold, onDelayedClick, onDebtClick, onLoopsClick }) {
  const reduced = useReducedMotion();

  const threshold = Number(delayThreshold);
  const delayedCount = metrics.responseDetails
    ? metrics.responseDetails.filter((d) => d.responseMin >= threshold).length
    : 0;

  const stats = [
    {
      label: 'Emails Received',
      value: metrics.totalReceived.toLocaleString(),
      icon: Inbox,
      color: '#6366f1',
      bg: isDark ? 'rgba(99,102,241,0.12)' : '#eef2ff',
      info: 'Total emails received in your mailbox during the selected date range. Includes inbox, archived, and categorized emails.',
    },
    {
      label: 'Emails Sent',
      value: metrics.totalSent.toLocaleString(),
      icon: Send,
      color: '#10b981',
      bg: isDark ? 'rgba(16,185,129,0.12)' : '#ecfdf5',
      info: 'Total emails you sent during the selected date range, including replies and forwards.',
    },
    {
      label: 'Avg Response Time',
      value: fmtTime(metrics.avgResponseTime),
      icon: Clock,
      color: '#f59e0b',
      bg: isDark ? 'rgba(245,158,11,0.12)' : '#fffbeb',
      info: 'Average time you took to reply to received emails. Only counts replies sent within 7 days of the original email.',
    },
    {
      label: 'Median Response',
      value: fmtTime(metrics.medianResponseTime),
      icon: TrendingUp,
      color: '#3b82f6',
      bg: isDark ? 'rgba(59,130,246,0.12)' : '#eff6ff',
      info: 'The middle value of all your response times. Less affected by outliers than the average — a better measure of your typical reply speed.',
    },
    {
      label: 'Delayed Responses',
      value: delayedCount.toLocaleString(),
      icon: AlarmClock,
      color: '#ef4444',
      bg: isDark ? 'rgba(239,68,68,0.12)' : '#fef2f2',
      clickable: true,
      onClick: onDelayedClick,
      info: `Emails where your reply took longer than the selected threshold. Click to view the full list.`,
    },
    {
      label: 'Email Debt',
      value: (metrics.emailDebt?.length ?? 0).toLocaleString(),
      icon: MailOpen,
      color: '#f97316',
      bg: isDark ? 'rgba(249,115,22,0.12)' : '#fff7ed',
      clickable: true,
      onClick: onDebtClick,
      info: 'Received emails you haven\'t replied to yet. Deduplicated by conversation — only counts one per thread.',
    },
    {
      label: 'Pending Loops',
      value: (metrics.pendingLoops?.length ?? 0).toLocaleString(),
      icon: RefreshCw,
      color: '#8b5cf6',
      bg: isDark ? 'rgba(139,92,246,0.12)' : '#f5f3ff',
      clickable: true,
      onClick: onLoopsClick,
      info: 'Email conversations still waiting for a response — either from you or from the other person.',
    },
    {
      label: 'Closed Loops',
      value: (metrics.closedLoops?.length ?? 0).toLocaleString(),
      icon: CheckCircle,
      color: '#10b981',
      bg: isDark ? 'rgba(16,185,129,0.12)' : '#ecfdf5',
      info: 'Completed email conversations where both sides participated and no response has been pending for over 48 hours.',
    },
  ];

  return (
    <motion.div
      variants={reduced ? {} : STAGGER_CONTAINER}
      initial="hidden"
      animate="visible"
      style={{
        display: 'grid',
        gridTemplateColumns: 'repeat(4, 1fr)',
        gap: 16,
        marginBottom: 28,
      }}
    >
      {stats.map(({ label, value, icon: Icon, color, bg, clickable, onClick, info }) => (
        <motion.div
          key={label}
          variants={reduced ? {} : FADE_UP}
          whileHover={{ y: -2, transition: { duration: 0.15 } }}
          onClick={clickable && onClick ? onClick : undefined}
          style={{ cursor: clickable ? 'pointer' : 'default' }}
        >
          <div
            style={{
              borderRadius: 14,
              padding: '20px',
              height: '100%',
              background: isDark ? 'rgba(255,255,255,0.03)' : '#ffffff',
              border: `1px solid ${isDark ? 'rgba(255,255,255,0.06)' : '#f0f0f0'}`,
              boxShadow: isDark
                ? '0 1px 3px rgba(0,0,0,0.2)'
                : '0 1px 3px rgba(0,0,0,0.04), 0 1px 2px rgba(0,0,0,0.02)',
              transition: 'all 0.2s ease',
              position: 'relative',
              overflow: 'hidden',
            }}
            onMouseEnter={(e) => {
              e.currentTarget.style.borderColor = `${color}40`;
              e.currentTarget.style.boxShadow = isDark
                ? `0 4px 16px rgba(0,0,0,0.3), 0 0 0 1px ${color}25`
                : `0 4px 16px ${color}12, 0 1px 3px rgba(0,0,0,0.06)`;
            }}
            onMouseLeave={(e) => {
              e.currentTarget.style.borderColor = isDark ? 'rgba(255,255,255,0.06)' : '#f0f0f0';
              e.currentTarget.style.boxShadow = isDark
                ? '0 1px 3px rgba(0,0,0,0.2)'
                : '0 1px 3px rgba(0,0,0,0.04), 0 1px 2px rgba(0,0,0,0.02)';
            }}
          >
            {/* Left accent line */}
            <div style={{
              position: 'absolute',
              top: 12,
              left: 0,
              bottom: 12,
              width: 3,
              borderRadius: '0 3px 3px 0',
              background: color,
              opacity: 0.7,
            }} />

            {/* Header: label + info */}
            <div style={{
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'space-between',
              marginBottom: 16,
            }}>
              <span style={{
                fontSize: 13,
                fontWeight: 500,
                color: isDark ? 'rgba(255,255,255,0.55)' : '#6b7280',
                lineHeight: 1,
              }}>
                {label}
              </span>
              {info && (
                <Tooltip title={info} placement="top" overlayStyle={{ maxWidth: 280 }}>
                  <Info
                    size={14}
                    color={isDark ? 'rgba(255,255,255,0.2)' : '#d1d5db'}
                    style={{ cursor: 'help', flexShrink: 0 }}
                  />
                </Tooltip>
              )}
            </div>

            {/* Value row: number + icon */}
            <div style={{
              display: 'flex',
              alignItems: 'flex-end',
              justifyContent: 'space-between',
            }}>
              <span style={{
                fontSize: 30,
                fontWeight: 700,
                lineHeight: 1,
                letterSpacing: '-0.02em',
                color: isDark ? '#f3f4f6' : '#111827',
              }}>
                {value}
              </span>

              <div style={{
                width: 40,
                height: 40,
                borderRadius: 10,
                background: bg,
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                flexShrink: 0,
              }}>
                <Icon size={20} color={color} strokeWidth={1.8} />
              </div>
            </div>

            {/* Clickable footer */}
            {clickable && (
              <div style={{
                display: 'flex',
                alignItems: 'center',
                gap: 4,
                marginTop: 12,
                paddingTop: 12,
                borderTop: `1px solid ${isDark ? 'rgba(255,255,255,0.06)' : '#f3f4f6'}`,
              }}>
                <span style={{
                  fontSize: 12,
                  fontWeight: 500,
                  color: color,
                }}>
                  View details
                </span>
                <ArrowRight size={12} color={color} />
              </div>
            )}
          </div>
        </motion.div>
      ))}
    </motion.div>
  );
}
